import { getErrorFromObject } from '../logging/genericInformationLogging';
import { DatapointStatus, PerformanceDatapoint } from 'owa-analytics';
import { GetAuthorizedFilePreviewUrlJsonResponse } from 'owa-group-files-hub-store';
import { ensureAuthToken } from 'owa-groups-sharepoint-commands/lib/authentication';
import { HttpReadyState, HttpStatusCode } from 'owa-http-status-codes';
import { parse as parseUrl } from 'url';

const AUTHORIZATION_TAG = 'Authorization';
const BEARER_TOKEN_HEADER_PREFIX = 'Bearer ';
const GET_PREVIEW_URL_PREFIX = '/_api/v2.0/shares/';
const GET_PREVIEW_URL_POSTFIX = '/driveItem/preview';

export default function getAuthorizedFilePreviewUrl(
    fileUrl: string,
    actionSource: 'Prefetch' | 'PreviewInNewSxS'
): Promise<string> {
    return new Promise<string>(async (resolve, reject) => {
        const datapoint = new PerformanceDatapoint('GroupFilesHubGetAuthorizedFilePreviewUrlDP', {
            isCore: true,
        });
        datapoint.addCustomProperty('actionSource', actionSource);
        let groupSiteRootUrl;
        if (fileUrl) {
            const groupSiteUrl = parseUrl(fileUrl, false, true);
            groupSiteRootUrl = `${groupSiteUrl.protocol}//${groupSiteUrl.host}`;
            if (!groupSiteRootUrl || groupSiteRootUrl.length == 0) {
                datapoint.addCustomProperty('groupSiteRootUrl', 'nullOrEmpty');
            }
        } else {
            const error = new Error('Group file url is not valid');
            datapoint.endWithError(DatapointStatus.ClientError, getErrorFromObject(error));
            reject(error);
            return;
        }

        let accessToken;
        try {
            datapoint.addCustomProperty('getToken', 'Started');
            accessToken = await ensureAuthToken(groupSiteRootUrl, 'GroupFilesHubSPPreviewUrl');
            datapoint.addCustomProperty('getToken', 'Finished');
            if (!accessToken) {
                throw new Error('AccessToken is null or empty.');
            }
        } catch (error) {
            datapoint.endWithError(DatapointStatus.ServerError, getErrorFromObject(error));
            reject(new Error('Failed to get SP access token.'));
            return;
        }

        const url = buildSharePointPreviewRequestUrl(groupSiteRootUrl, fileUrl);
        const request = new XMLHttpRequest();
        request.open('POST', url, true);
        request.setRequestHeader(AUTHORIZATION_TAG, BEARER_TOKEN_HEADER_PREFIX + accessToken);
        request.onreadystatechange = function() {
            if (request.readyState === HttpReadyState.HeadersReceived) {
                datapoint.addCustomProperty(
                    'sprequestguid',
                    request.getResponseHeader('sprequestguid')
                );
            }

            if (request.readyState === HttpReadyState.Done) {
                if (request.status === HttpStatusCode.OK) {
                    let response: GetAuthorizedFilePreviewUrlJsonResponse;
                    try {
                        response = JSON.parse(request.response);
                    } catch (error) {
                        datapoint.endWithError(
                            DatapointStatus.ClientError,
                            getErrorFromObject(error)
                        );
                        reject(new Error('Failed to parse response.'));
                        return;
                    }

                    datapoint.end();
                    resolve(response.getUrl);
                } else {
                    datapoint.endWithError(
                        DatapointStatus.ServerError,
                        new Error(request.responseText)
                    );
                    reject(
                        new Error(
                            'Request failed with status ' +
                                request.status +
                                '. Message: ' +
                                request.responseText
                        )
                    );
                }
            }
        };

        request.send();
    });
}

export function buildSharePointPreviewRequestUrl(
    groupSiteRootUrl: string,
    fileUrl: string
): string {
    let base64Value = window.btoa(unescape(encodeURIComponent(fileUrl)));
    base64Value = base64Value.replace(/\//g, '_').replace(/\+/g, '-');
    let endIndex = base64Value.length - 1;
    while (endIndex >= 0 && base64Value.charAt(endIndex) == '=') {
        endIndex--;
    }
    const encodedUrl = 'u!' + base64Value.slice(0, endIndex + 1);
    // Example request url: https://microsoft.sharepoint.com/_api/v2.0/shares/u!aHR0cHM6Ly9taWNyb3NvZnQuc2hhcmVwb2ludC5jb20vdGVhbXMvZnl0ZXN0MS9TaGFyZWQgRG9jdW1lbnRzL0dlbmVyYWwvaGFtc3Rlci5wZGY/driveItem/preview
    return groupSiteRootUrl + GET_PREVIEW_URL_PREFIX + encodedUrl + GET_PREVIEW_URL_POSTFIX;
}
